import dayjs from 'dayjs'

import { DISK_ADMIN_QUOTA_CRITICAL_PERCENT, DISK_ADMIN_QUOTA_WARN_PERCENT } from '../Contracts/DiskAdmin'

import type { DiskAdminStorageState, DiskAdminStorageType } from '../Contracts/DiskAdmin'

/** «29.10.2026»; без дати — прочерк. */
export const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '—'
    const date = dayjs(iso)

    return date.isValid() ? date.format('DD.MM.YYYY') : '—'
}

/** «29.10.2026, 14:05». */
export const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—'
    const date = dayjs(iso)

    return date.isValid() ? date.format('DD.MM.YYYY, HH:mm') : '—'
}

/** «29.10» — підпис осі тренду. */
export const formatDayShort = (day: string): string => (day.length >= 10 ? `${day.slice(8, 10)}.${day.slice(5, 7)}` : day)

/**
 * Остання активність для таблиці: «сьогодні», «вчора», «5 дн. тому» (до 30
 * днів), далі — дата. Без активності — «немає».
 */
export const activityLabel = (iso: string | null | undefined, now: Date = new Date()): string => {
    if (!iso) return 'немає'
    const date = dayjs(iso)

    if (!date.isValid()) return 'немає'

    const days = dayjs(now).startOf('day').diff(date.startOf('day'), 'day')

    if (days <= 0) return 'сьогодні'
    if (days === 1) return 'вчора'
    if (days <= 30) return `${days} дн. тому`

    return date.format('DD.MM.YYYY')
}

export type QuotaTone = 'normal' | 'warning' | 'error'

/** Колір смуги квоти за порогами сповіщень: < 80 % — серія, 80–95 % — попередження, ≥ 95 % — помилка. */
export const quotaTone = (percent: number | null): QuotaTone => {
    if (percent === null) return 'normal'
    if (percent >= DISK_ADMIN_QUOTA_CRITICAL_PERCENT) return 'error'
    if (percent >= DISK_ADMIN_QUOTA_WARN_PERCENT) return 'warning'

    return 'normal'
}

/** Відсоток заповнення з одним знаком для малих часток: «0,2 %», «85 %». */
export const formatPercent = (percent: number | null): string => {
    if (percent === null) return '—'
    if (percent > 0 && percent < 1) return '< 1 %'

    return `${Math.round(percent)} %`
}

/** Частка від цілого у відсотках, 0…100. */
export const shareOf = (part: number, total: number): number => (total > 0 ? (Math.max(0, part) / total) * 100 : 0)

export const QUOTA_UNITS = ['МБ', 'ГБ', 'ТБ'] as const

export type QuotaUnit = (typeof QUOTA_UNITS)[number]

const UNIT_BYTES: Record<QuotaUnit, number> = { МБ: 1024 ** 2, ГБ: 1024 ** 3, ТБ: 1024 ** 4 }

/** Байти → число й одиниця для поля квоти: 10 ГБ → `{10, 'ГБ'}`, 1,5 ГБ → `{1.5, 'ГБ'}`, 512 МБ → `{512, 'МБ'}`. */
export const splitQuota = (bytes: number): { value: number; unit: QuotaUnit } => {
    for (const unit of [...QUOTA_UNITS].reverse()) {
        const hundredths = (bytes / UNIT_BYTES[unit]) * 100

        if (hundredths >= 100 && Math.abs(hundredths - Math.round(hundredths)) < 1e-6) {
            return { value: Math.round(hundredths) / 100, unit }
        }
    }

    return { value: Math.round((bytes / UNIT_BYTES['МБ']) * 100) / 100, unit: 'МБ' }
}

/** Число з одиницею → цілі байти (бек приймає лише цілі). */
export const joinQuota = (value: number, unit: QuotaUnit): number => Math.round(Math.max(0, value) * UNIT_BYTES[unit])

/** Підпис типу простору в рядку й картці. */
export const STORAGE_TYPE_LABEL: Record<DiskAdminStorageType, string> = {
    personal: 'Мій диск',
    company: 'Компанія',
    department: 'Відділ',
    custom: 'Простір',
}

export interface StorageBadge {
    label: string
    tone: 'red' | 'orange' | 'blue' | 'default'
}

/**
 * Позначки рядка й шапки картки: «Звільнений» (власник деактивований),
 * «Заморожений» / «Архів» / «Передано» (стан простору), «Немає диска».
 * Активний диск активного співробітника — без позначок.
 */
export const storageBadges = (state: DiskAdminStorageState, departed: boolean, hasDisk: boolean): StorageBadge[] => {
    const badges: StorageBadge[] = []

    if (departed) badges.push({ label: 'Звільнений', tone: 'red' })
    if (state === 'frozen') badges.push({ label: 'Заморожений', tone: 'orange' })
    if (state === 'archived') badges.push({ label: 'Архів', tone: 'blue' })
    if (state === 'transferred') badges.push({ label: 'Передано', tone: 'default' })
    if (!hasDisk) badges.push({ label: 'Немає диска', tone: 'default' })

    return badges
}
